/**
 * How long a password lasts, and who that applies to.
 *
 * In `shared` because the API enforces it and the portal explains it, and a
 * warning banner that disagrees with the server about the expiry date is worse
 * than no banner: somebody is told they have a fortnight and is locked out on
 * Tuesday.
 *
 * The numbers are policy, not physics. They are expected to be argued about and
 * changed, which is exactly why they are named constants in one file rather
 * than literals spread across a service, a middleware and three components.
 */
/** Asked for on 13 Sep 2026: volunteer and team credentials expire this often. */
export const PASSWORD_MAX_AGE_DAYS = 120;
/**
 * When the portal starts mentioning it.
 *
 * Two weeks, because the people this applies to are volunteers who may sign in
 * once a fortnight. A three-day warning would be invisible to exactly the
 * accounts most likely to be surprised by a lock-out.
 */
export const PASSWORD_WARNING_DAYS = 14;
/** When the warning stops being a note and becomes an alert. */
export const PASSWORD_URGENT_DAYS = 5;
/**
 * The shortest acceptable password.
 *
 * 12 until 13 Sep 2026, when it was lowered to 8 alongside the four character
 * rules below. Worth being clear about the trade: length buys more against a
 * guessing attack than composition does, so eight-with-complexity is weaker
 * than the twelve it replaces, not stronger. It is a deliberate choice about
 * what volunteers will actually put up with rather than an improvement.
 *
 * Changing this number changes the rule everywhere — the API, both request
 * schemas, the checklist in the portal and the label inside it — which is the
 * reason it is a constant and not a literal.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_RULES = [
    {
        id: 'length',
        label: `At least ${PASSWORD_MIN_LENGTH} characters`,
        test: (password) => password.length >= PASSWORD_MIN_LENGTH,
    },
    {
        id: 'lowercase',
        label: 'A lowercase letter',
        test: (password) => /[a-z]/.test(password),
    },
    {
        id: 'uppercase',
        label: 'An uppercase letter',
        test: (password) => /[A-Z]/.test(password),
    },
    {
        id: 'number',
        label: 'A number',
        test: (password) => /[0-9]/.test(password),
    },
    {
        id: 'symbol',
        label: 'A symbol, such as ! ? - or @',
        test: (password) => /[^A-Za-z0-9]/.test(password),
    },
];
/** Each rule with whether this password satisfies it. Drives the checklist. */
export function checkPassword(password) {
    return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) }));
}
export function isPasswordValid(password) {
    return PASSWORD_RULES.every((rule) => rule.test(password));
}
/**
 * The unmet rules, as a sentence the API can return.
 *
 * Every unmet rule at once rather than the first one. A server that refuses a
 * password for its missing digit, then for its missing capital, then for its
 * length, is three round trips to learn what one message could have said —
 * and the form already shows all five, so a single-rule error would not even
 * agree with what is on screen.
 */
export function passwordProblems(password) {
    return PASSWORD_RULES.filter((rule) => !rule.test(password)).map((rule) => rule.label);
}
/**
 * Whose passwords age out.
 *
 * ADMIN is excluded by name rather than by a list of the included roles, so a
 * role added later expires by default. The safer direction to be wrong in: a
 * new role that expires when it need not is a nuisance, and one that never
 * expires because somebody forgot to add it to a list is the bug this rule
 * exists to prevent.
 */
export function passwordExpires(role) {
    return role !== 'ADMIN';
}
/** Whether the portal should say anything at all. */
export function shouldWarn(status) {
    return status.days_remaining !== null && status.days_remaining <= PASSWORD_WARNING_DAYS;
}
/** Whether what it says should be an alert rather than a note. */
export function isUrgent(status) {
    return status.days_remaining !== null && status.days_remaining <= PASSWORD_URGENT_DAYS;
}
//# sourceMappingURL=password-policy.js.map